import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import CollegeCoachViewsTab, { COLLEGE_COACH_VIEWS_INLINE_PAGE_SIZE } from './components/CollegeCoachViewsTab';
import InterestedInMeTab, { INTERESTED_IN_ME_INLINE_PAGE_SIZE } from './components/InterestedInMeTab';
import LockedTabContent from './components/LockedTabContent';
import Img from '../../Img';
import GenericPopup from '../../PopUps/GenericPopup';

import { showPopupNew } from '../../../actions/popup-actions';
import { PopupTypes } from '../../../utils/constants';
import { PowerFunnelGatedFeatures, PowerFunnelSurfaces } from '../../Analytics/constants';
import { useCPPDynamicConfig } from '../../hooks/useCPPDynamicConfig';
import styles from './collegeCoachActivity.scss';

const SUBSCRIPTION_TIER_YEAR = 'year';
const LOCK_ICON_SRC = '/img/v2/icons/icon-feature-lock.svg';
const TESTID_PREFIX = 'profile.college-coach-activity';
export const COLLEGE_COACH_ACTIVITY_FULL_LIST_PATH = '/my-profile/college-prospect/college-coach-activity';

export const TAB_COACH_VIEWS = 'coach-views';
export const TAB_INTERESTED_IN_ME = 'interested-in-me';

const TAB_DEFINITIONS = [
    { key: TAB_INTERESTED_IN_ME, label: 'Interested in Me' },
    { key: TAB_COACH_VIEWS, label: 'College Coach Views' },
];

const CollegeCoachActivity = ({ onAddSuccess }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.auth?.user);
    const claimedPlayerId = useSelector((state) => state.auth?.user?.claimedPlayer?.playerId);
    const legacyProfileViewsTotal = useSelector((state) => state.profile?.profileViews?.total) || 0;
    const gender = user?.gender || null;
    const { isCollegeProspectPowerEnabled, showCollegeCoachActivity } = useCPPDynamicConfig();

    const isPowered = !!user?.isPowered;
    const hasAnnualPower = isPowered && user?.subscriptionTierType === SUBSCRIPTION_TIER_YEAR;

    const isViewsUnlocked = isPowered;
    const isInterestedUnlocked = hasAnnualPower;

    const [activeTab, setActiveTab] = useState(() => (
        isInterestedUnlocked ? TAB_INTERESTED_IN_ME : TAB_COACH_VIEWS
    ));
    const [addErrorMessage, setAddErrorMessage] = useState(null);
    const [viewsTotal, setViewsTotal] = useState(0);
    const [interestedTotal, setInterestedTotal] = useState(0);

    const handleViewsTotal = useCallback((total) => setViewsTotal(total || 0), []);
    const handleInterestedTotal = useCallback((total) => setInterestedTotal(total || 0), []);

    const handleAddError = (message) => {
        setAddErrorMessage(message);
    };

    const handleCloseAddError = () => {
        setAddErrorMessage(null);
    };

    const handleUnlock = (gatedFeature) => {
        const popup = isPowered
            ? PopupTypes.POWER_MONTHLY_UPGRADE_POPUP
            : PopupTypes.UPGRADE_TO_POWER_POPUP;
        dispatch(showPopupNew({
            popup,
            referringSurface: PowerFunnelSurfaces.PLAYER_PROFILE_COLLEGE_PROSPECT,
            gatedFeature,
        }));
    };

    const isTabLocked = (tabKey) => {
        if (tabKey === TAB_COACH_VIEWS) {
            return !isViewsUnlocked;
        }
        return !isInterestedUnlocked;
    };

    const handleTabClick = (tabKey) => {
        setActiveTab(tabKey);
        if (isTabLocked(tabKey)) {
            const gatedFeature = tabKey === TAB_COACH_VIEWS
                ? PowerFunnelGatedFeatures.COLLEGE_COACH_VIEWS
                : PowerFunnelGatedFeatures.COLLEGE_PROSPECT_TARGET_LIST;
            handleUnlock(gatedFeature);
        }
    };

    const activeTabIsLocked = useMemo(() => isTabLocked(activeTab), [activeTab, isViewsUnlocked, isInterestedUnlocked]);
    const showViewAll = viewsTotal >= COLLEGE_COACH_VIEWS_INLINE_PAGE_SIZE ||
        interestedTotal >= INTERESTED_IN_ME_INLINE_PAGE_SIZE;

    if (!isCollegeProspectPowerEnabled || !showCollegeCoachActivity) {
        return null;
    }

    const renderActiveContent = () => {
        if (activeTab === TAB_COACH_VIEWS) {
            if (!isViewsUnlocked) {
                const viewsNoun = legacyProfileViewsTotal === 1 ? 'View' : 'Views';
                return (
                    <LockedTabContent
                        summary="See which college coaches have viewed your profile."
                        headline={`${legacyProfileViewsTotal} College Coach ${viewsNoun}`}
                        testIdPrefix={`${TESTID_PREFIX}.coach-views`}
                    />
                );
            }
            if (!claimedPlayerId) {
                return null;
            }
            return (
                <CollegeCoachViewsTab
                    playerId={claimedPlayerId}
                    gender={gender}
                    onAddError={handleAddError}
                    onAddSuccess={onAddSuccess}
                    onTotalChange={handleViewsTotal}
                />
            );
        }
        if (!isInterestedUnlocked) {
            return (
                <LockedTabContent
                    summary="See which college coaches have shown active interest in you and viewed your profile."
                    testIdPrefix={`${TESTID_PREFIX}.interested-in-me`}
                />
            );
        }
        return (
            <InterestedInMeTab
                gender={gender}
                onAddError={handleAddError}
                onAddSuccess={onAddSuccess}
                onTotalChange={handleInterestedTotal}
            />
        );
    };

    return (
        <div
            className={styles.collegeCoachActivityContainer}
            data-testid={`${TESTID_PREFIX}.container`}
        >
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h6
                        className={styles.title}
                        data-testid={`${TESTID_PREFIX}.title`}
                    >
                        college coach activity
                    </h6>
                </div>
                {showViewAll && (
                    <Link
                        to={{
                            pathname: COLLEGE_COACH_ACTIVITY_FULL_LIST_PATH,
                            search: `?tab=${activeTab}`,
                        }}
                        className={styles.viewAllLink}
                        data-testid={`${TESTID_PREFIX}.view-all-link`}
                    >
                        View All
                    </Link>
                )}
            </div>

            <div className={styles.tabBar} role="tablist" data-testid={`${TESTID_PREFIX}.tab-bar`}>
                {TAB_DEFINITIONS.map((tab) => {
                    const isActive = activeTab === tab.key;
                    const isLocked = isTabLocked(tab.key);
                    const className = `${styles.tabPill} ${isActive ? styles.tabPillActive : ''}`.trim();
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            className={className}
                            onClick={() => handleTabClick(tab.key)}
                            data-testid={`${TESTID_PREFIX}.tab-${tab.key}`}
                        >
                            <span>{tab.label}</span>
                            {isLocked && (
                                <Img
                                    src={LOCK_ICON_SRC}
                                    alt=""
                                    className={styles.tabPillLockIcon}
                                />
                            )}
                        </button>
                    );
                })}
            </div>

            <div
                role="tabpanel"
                data-testid={`${TESTID_PREFIX}.tab-panel-${activeTab}`}
                data-locked={activeTabIsLocked ? 'true' : 'false'}
            >
                {renderActiveContent()}
            </div>

            {addErrorMessage && (
                <GenericPopup
                    title="Couldn't Add College"
                    subtitle={addErrorMessage}
                    buttonText="Okay"
                    onButtonClick={handleCloseAddError}
                    onRequestClose={handleCloseAddError}
                    data-testid={`modal.${TESTID_PREFIX}.add-college-error`}
                />
            )}
        </div>
    );
};

CollegeCoachActivity.defaultProps = {
    onAddSuccess: null,
};

CollegeCoachActivity.propTypes = {
    onAddSuccess: PropTypes.func,
};

export default CollegeCoachActivity;
